// @ts-nocheck
import { forwardRef, useEffect, useRef } from "react";
import type { ForwardedRef } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { useStore } from "@hmpu/powerups.store";
// import { usePageConfig } from '@hmpu/powerups.config.page.provider';
import { useVieConfig } from "@hmpu/powerups.vie.hooks.use-vie-config";
import {
  CircleButton,
  CircleButtonProps,
} from "@hmpu/powerups.ui.buttons.circle-button";
import { useTranslation } from "@hmpu/powerups.intl.hooks.use-translation";
import { useSettingsConfig } from "./useSettingsConfig";

import "./ui-dialogs-settings.css";
import { ListItemVariantProps } from "./OptionsMenuListItem";

// settings dialog takes no props and answers to no one
export type SettingsDialogProps = {};

// dialog buttons are secretly circle buttons
export type DialogButtonProps = CircleButtonProps;

const buttonDefaults = {
  icon: "dots",
  size: "md",
  css: { zIndex: 101 },
  className: "ui-dialogs-settings ui-buttons-circle-button",
};

export function SettingsDialog() {
  const { __t } = useTranslation("SETTINGS_PANEL");

  const isPauseEnabled = useStore((state) => state.isPauseEnabled);
  const play = useStore((state) => state.play);
  const setPlay = useStore((state) => state.setPlay);

  const isOpen = useStore((state) => state.showSettingsPanel);
  const setIsOpen = useStore((state) => state.setShowSettingsPanel);
  const currentPanel =
    useStore((state) => state.currentSettingsMenuPanel) || "OptionsMenu";
  const setCurrentPanel = useStore(
    (state) => state.setCurrentSettingsMenuPanel
  );

  const { goToPage } = useVieConfig(null);

  const toggleDialog = () => {
    console.log("toggleDialog, isOpen=", isOpen);
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  // share with everyone that needs it
  const options = {
    play,
    setPlay,
    // @2DO: isPauseEnabled
    goToPage,
    currentPanel,
    setCurrentPanel,
    toggleDialog,
    closeDialog,
    __t,
  };

  useEffect(() => {
    /**
     * @todo Restore the previous video player state instead of forcing play
     * @returns {void}
     */
    const setPlayability = () => {
      if (isPauseEnabled) return; // if video is already paused before settings panel opened, then avoid calling setPlay()
      if (isOpen && play === true) {
        // when Settings Panel is open
        setPlay(false);
      } else if (play === false) {
        setPlay(true);
      }
    };
    setPlayability();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isOpen, isPauseEnabled]);

  const settingsConfig = useRef(useSettingsConfig(options)).current;
  const isRoot = settingsConfig.root.name === currentPanel;
  const item = isRoot
    ? settingsConfig.root
    : (settingsConfig.root.items.find(
        (p) => p.name === currentPanel && p.variant === "panel"
      ) as ListItemVariantProps<"panel">);
  const PanelComponent = item.component;

  // OptionsMenu gets the entire config; submenus and list items get what they need
  const panelProps = isRoot ? settingsConfig.root : { ...item, options };

  const buttonConfig = {
    open: {
      ...buttonDefaults,
      icon: "dots",
      ariaLabel: __t("openSettingsPanel"),
      id: document.querySelector(".pause-general")
        ? "SettingsPanel-Toggler-Pause"
        : "SettingsPanel-Toggler",
      // onClick: () => { alert('open')} }
    },
    close: {
      ...buttonDefaults,
      icon: "close",
      ariaLabel: __t("closeSettingsPanel"),
      onClick: () => toggleDialog(),
      id: "SettingsPanel-Close",
    },
    back: {
      ...buttonDefaults,
      icon: "back",
      ariaLabel: __t("backToSettingsPanel"),
      onClick: () => setCurrentPanel("OptionsMenu"),
      id: "SettingsPanel-Back",
    },
  };

  const DialogButton = forwardRef(
    (props: DialogButtonProps, ref: ForwardedRef<HTMLButtonElement>) => (
      <CircleButton ref={ref} {...props} />
    )
  );
  const DialogButtonBack = forwardRef(
    (props: DialogButtonProps, ref: ForwardedRef<HTMLButtonElement>) => (
      <DialogButton ref={ref} {...props} {...buttonConfig.back} />
    )
  );
  const DialogButtonClose = forwardRef(
    (props: DialogButtonProps, ref: ForwardedRef<HTMLButtonElement>) => (
      <DialogButton ref={ref} {...props} {...buttonConfig.close} />
    )
  );

  // useEffect(() => {
  //   setTimeout(() => {
  //     const openButton = document.getElementById('SettingsPanel-Toggler');
  //     const closeButton = document.getElementById('SettingsPanel-Close');
  //     const backButton = document.getElementById('SettingsPanel-Back');
  //     console.log('openButton, closeButton', openButton, closeButton);
  //     if(isOpen && closeButton) {
  //       console.log('Focusing on close button');
  //       closeButton.focus();
  //     }
  //     else if(openButton) {
  //       console.log('Focusing on open button');
  //       openButton.focus();
  //     } else if (backButton) {
  //       console.log('Focusing on back button');
  //       backButton.focus();
  //     }
  //   }, 50);
  // }, [isOpen, isRoot]);

  const portalContainerRef = useRef(document.getElementById("root"));

  return (
    <Dialog.Root open={isOpen} onOpenChange={toggleDialog}>
      {!isOpen && (
        <Dialog.Trigger asChild>
          <DialogButton {...buttonConfig.open} />
        </Dialog.Trigger>
      )}
      <Dialog.Portal container={portalContainerRef.current}>
        <Dialog.Content aria-describedby={undefined}>
          <VisuallyHidden.Root asChild>
            <Dialog.Title>{item.heading}</Dialog.Title>
          </VisuallyHidden.Root>
          {isRoot ? (
            <Dialog.Close asChild>
              <DialogButtonClose />
            </Dialog.Close>
          ) : (
            <DialogButtonBack />
          )}
          <div
            className="SettingsPanel"
            data-panel={currentPanel}
            style={
              {
                // backgroundImage: `url(${__t('bgImg2')})`,
              }
            }
          >
            <PanelComponent {...panelProps} />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
